import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpStatusCode,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BYPASS_ERROR } from '@app/models/http-context.model';
import { SecurityService } from '@app/services/security.service';
import { NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/toast.service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import packageJson from '@root/package.json';
import { environment } from '../../environments/environment';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
    private securityService = inject(SecurityService);
    private translateService = inject(TranslateService);
    private toastService = inject(ToastService);
    private navCtrl = inject(NavController);

    private static _redirectInProgress = false;

    public static get redirectInProgress() {
        return HttpConfigInterceptor._redirectInProgress;
    }
    public static set redirectInProgress(value) {
        HttpConfigInterceptor._redirectInProgress = value;
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const headers = request.headers.append('App-Version', packageJson.version);
        request = request.clone({
            withCredentials: true,
            headers
        });
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                const backendUrl = environment.urlBackend;
                const remoteBackend = backendUrl && request.urlWithParams.startsWith(backendUrl);
                const localBackend = request.urlWithParams.startsWith(location.protocol + '//') === false;

                if (remoteBackend || localBackend) {
                    this.handlingErrorCode(request, error);
                }
                return throwError(() => error);
            }),
        );
    }

    private handlingErrorCode(request: HttpRequest<any>, httpErrorResponse: HttpErrorResponse) {
        const error: string =
            typeof httpErrorResponse.error === 'string' ? httpErrorResponse.error : httpErrorResponse.error?.title;
        const errorMessage =
            this.translateService.instant(`ERROR.${error}`) !== `ERROR.${error}`
                ? this.translateService.instant(`ERROR.${error}`)
                : this.translateService.instant('ERROR.GENERIQUE');
        switch (httpErrorResponse.status) {
            case HttpStatusCode.Unauthorized:
                if (!HttpConfigInterceptor.redirectInProgress) {
                    console.log('[HttpConfigInterceptor] Redirection vers la page de login ...');
                    HttpConfigInterceptor.redirectInProgress = true;
                    this.securityService.logout().then(() => {
                        setTimeout(() => {
                            HttpConfigInterceptor.redirectInProgress = false;
                            this.navCtrl.navigateRoot('/login');
                        }, 200);
                    });

                    // si on souhaite afficher un message d'erreur spécifié côté front, on affiche pas le message back
                    if (!request.context.get(BYPASS_ERROR)) {
                        this.toastService.error({
                            message: this.translateService.instant('ERROR.401'),
                        });
                    }
                } else {
                    console.log('[HttpConfigInterceptor] Redirection déjà en cours ...');
                }
                break;
            // messages d'erreurs à afficher aux clients (on affiche le message donné par le serveur)
            case HttpStatusCode.BadRequest:
            case HttpStatusCode.PreconditionFailed:
            case HttpStatusCode.Forbidden:
            case HttpStatusCode.Conflict:
            case HttpStatusCode.NotFound:
                if (!request.context.get(BYPASS_ERROR)) {
                    this.toastService.error({
                        message: errorMessage,
                    });
                }
                break;
            default:
                // Ajout du status !== 0 pour enlever l'erreur affichée au lancement de l'app sur iOS :
                if (!request.context.get(BYPASS_ERROR) && httpErrorResponse.status !== 0) {
                    this.toastService.error({
                        message: this.translateService.instant('ERROR.GENERIQUE'),
                    });
                }
                break;
        }
    }
}
